// Contracts — kids cabinet AUTH (2026-06-28). Email magic-link (parents) +
// code-link registration (moderators); stateless HS256 JWT sessions. Shapes
// snapped from migrations/0002_cabinet.sql (users, magic_tokens, mod_invite_codes)
// + the old kids-worker routes/auth.ts. Ported to raw-fetch + raw-D1 worker.
import { z } from 'zod';
import { KidsRoleSchema } from './enums';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// TG @username: optional leading '@', then [A-Za-z0-9_] (Telegram's own rule; we're
// lenient on the min so a partial handle still saves). Normalized ('@' stripped)
// in the handler before storing. max 40 caps the raw input.
const TG_LOGIN_RE = /^@?[A-Za-z0-9_]{1,40}$/;
// IANA tz form: Area/Location (e.g. America/New_York, Europe/Moscow). We only shape
// -check here; the curated FE dropdown is the real source of valid zones.
const TZ_RE = /^[A-Za-z]+\/[A-Za-z_]+/;
// Latin display name — letters + space/apostrophe/hyphen, must start with a letter.
// Mirrors the packer's name-matching alphabet (see kids/session.ts NAME_RE).
const NAME_RE = /^[A-Za-z][A-Za-z '\-]*$/;

// Registration-form profile that rides INSIDE the magic link (magic_tokens.profile,
// 0015): the sessionStorage stash alone dies when the emailed link is opened on a
// different device, leaving users.name = email prefix (the "mr.teleportator" bug).
export const KidsAuthProfileSchema = z
  .object({
    name: z.string().trim().regex(NAME_RE).max(80).optional(),
    city: z.string().trim().max(120).optional(),
    tgLogin: z.string().trim().regex(TG_LOGIN_RE).max(40).optional(),
    timezone: z.string().trim().regex(TZ_RE).max(64).optional(),
  })
  .strict();
export type KidsAuthProfile = z.infer<typeof KidsAuthProfileSchema>;

// POST /api/kids/auth/request {email, invite?, profile?} → magic-link email.
// Response never reveals whether the email exists (enumeration guard). `invite` is
// the optional registration-by-link token (#/register?token=…): when present and
// valid, the emailed link carries the invite's role/office/pid into verify.
// `profile` is the registration form (name/city/tz/tg), applied on verify.
export const KidsAuthRequestReqSchema = z.object({
  email: z.string().trim().regex(EMAIL_RE).max(200),
  invite: z.string().trim().min(1).max(120).optional(),
  profile: KidsAuthProfileSchema.optional(),
});
export type KidsAuthRequestReq = z.infer<typeof KidsAuthRequestReqSchema>;

// POST /api/kids/auth/mod-register {email,code} → checks mod_invite_codes → link.
export const KidsAuthModRegisterReqSchema = z.object({
  email: z.string().trim().regex(EMAIL_RE).max(200),
  code: z.string().trim().min(1).max(120),
});
export type KidsAuthModRegisterReq = z.infer<typeof KidsAuthModRegisterReqSchema>;

// POST /api/kids/auth/login {email,password} → {token}. Password accounts are
// provisioned directly in the DB (users.password_hash); login verifies the hash
// and mints the same session JWT as the magic-link verify.
export const KidsAuthLoginReqSchema = z.object({
  email: z.string().trim().regex(EMAIL_RE).max(200),
  password: z.string().min(1).max(200),
});
export type KidsAuthLoginReq = z.infer<typeof KidsAuthLoginReqSchema>;
export const KidsAuthLoginResSchema = z.object({ token: z.string() });
export type KidsAuthLoginRes = z.infer<typeof KidsAuthLoginResSchema>;

// Both /request and /mod-register return the same opaque ack.
export const KidsAuthAckResSchema = z.object({ ok: z.literal(true) });
export type KidsAuthAckRes = z.infer<typeof KidsAuthAckResSchema>;

// GET /api/kids/auth/me (Bearer) → the authenticated user + their role union.
// `roles` = union of users.role + user_roles rows; the frontend builds the
// cabinet menu from it and lets multi-role users switch.
export const KidsMeUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  role: KidsRoleSchema,
  pid: z.string().nullable(),
  office: z.string().nullable(),
  country: z.string().nullable(),
  language: z.string().nullable(),
  roles: z.array(KidsRoleSchema),
  // cluster (atlanta|remote) of the user's office — drives office-segmented
  // feature gates (e.g. Remote-only backgrounds rotation). null = no office.
  cluster: z.string().nullable().optional(),
  // 0035: per-moderator faceval gate (#44), 0/1 from D1. 1 = the session form
  // requires the photo AND the duplicate check gates «Создать». MANAGER-set;
  // default 0 = photo optional, no faceval call (pre-#34 behaviour).
  facevalRequired: z.number().int().optional(),
});
export type KidsMeUser = z.infer<typeof KidsMeUserSchema>;

export const KidsMeResSchema = z.object({ user: KidsMeUserSchema });
export type KidsMeRes = z.infer<typeof KidsMeResSchema>;

// POST /api/kids/me/assign-pid (Bearer staff, REMOTE only) → self-assign the next
// free moderator PID (≥50; Atlanta PIDs <50 are handed out manually by us).
// Idempotent: returns the existing pid if the caller already has one.
export const KidsAssignPidResSchema = z.object({ ok: z.literal(true), pid: z.string() });
export type KidsAssignPidRes = z.infer<typeof KidsAssignPidResSchema>;

// POST /api/kids/me/profile { name?, city?, tgLogin?, timezone? } (Bearer) → writes
// the moderator profile fields onto the CALLER's own users row. Collected at
// registration-by-link and flushed once the session exists (survives the Google/
// email redirect via sessionStorage). All fields optional so a partial save works;
// each present field is trimmed + shape-checked. name is the Latin display name
// (overrides the OAuth/email-derived default); tgLogin + timezone feed the packer
// export and the TG scheduler; city is the human-readable location.
export const KidsMeProfileReqSchema = z
  .object({
    name: z.string().trim().regex(NAME_RE).max(80).optional(),
    city: z.string().trim().max(120).optional(),
    tgLogin: z.string().trim().regex(TG_LOGIN_RE).max(40).optional(),
    timezone: z.string().trim().regex(TZ_RE).max(64).optional(),
  })
  .strict();
export type KidsMeProfileReq = z.infer<typeof KidsMeProfileReqSchema>;

export const KidsMeProfileResSchema = z.object({ ok: z.literal(true) });
export type KidsMeProfileRes = z.infer<typeof KidsMeProfileResSchema>;

// POST /api/kids/me/password {currentPassword?, password} → set/change the
// caller's own sign-in password (Tim, 02.07: «фичу пароль ставить в профиле»).
// currentPassword is REQUIRED (and verified) server-side when the account
// already has one — a stolen Bearer token alone must not rotate the password;
// magic-link/Google accounts with no password yet set one without it.
export const KidsSetPasswordReqSchema = z
  .object({
    currentPassword: z.string().max(200).optional(),
    password: z.string().min(8).max(200),
  })
  .strict();
export type KidsSetPasswordReq = z.infer<typeof KidsSetPasswordReqSchema>;

// A password set/change revokes ALL sessions (bumps the session epoch, migration
// 0022) — so the caller's own current token is now stale too. The response hands
// back a freshly minted token at the new epoch; the web swaps it in so the device
// that changed the password stays signed in while every other device is logged out.
export const KidsSetPasswordResSchema = z.object({ ok: z.literal(true), token: z.string() });
export type KidsSetPasswordRes = z.infer<typeof KidsSetPasswordResSchema>;

// POST /api/kids/auth/logout-all (Bearer, empty body) → "log out all devices".
// Bumps the session epoch (revokes every outstanding token) and returns a fresh
// token so the CURRENT device stays signed in — i.e. "sign out everywhere else".
export const KidsLogoutAllReqSchema = z.object({}).strict();
export type KidsLogoutAllReq = z.infer<typeof KidsLogoutAllReqSchema>;
export const KidsLogoutAllResSchema = z.object({ ok: z.literal(true), token: z.string() });
export type KidsLogoutAllRes = z.infer<typeof KidsLogoutAllResSchema>;
